import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { GitBranch, Plus, Edit, ArrowLeft, ArrowUp, ArrowDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAssignmentRules } from '@/hooks/useAssignmentRules';
import { useDepartments } from '@/hooks/useDepartments';
import { useSystemRoles } from '@/hooks/useSystemRoles';
import { useAppUsers } from '@/hooks/useAppUsers';
import { useQueryClient } from '@tanstack/react-query';
import { MultiSelect } from '@/components/ui/multi-select';

const MARKETS = ['BR', 'US', 'EU'];
const BUSINESS_UNITS = [
  { value: 'br', label: 'Brasil (br)' },
  { value: 'us', label: 'US (us)' },
];
const SOURCES = ['facebook', 'google', 'instagram', 'linkedin', 'website', 'referral', 'whatsapp', 'landing_page', 'make', 'reactivation'];
const STRATEGIES = [
  { value: 'round_robin', label: 'Round Robin (Balanceado)' },
  { value: 'random', label: 'Aleatório' },
];

export default function AssignmentRulesSettings() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { rules, isLoading } = useAssignmentRules();
  const { departments } = useDepartments();
  const { roles } = useSystemRoles();
  const { users } = useAppUsers();
  const [showDialog, setShowDialog] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return userId?.slice(0, 8);
    return user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Sem nome';
  };

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
    priority: 0,
    market: '',
    business_unit: '',
    source: '',
    origem: '',
    target_department_id: '',
    target_role_id: '',
    assignment_strategy: 'round_robin',
    selected_user_ids: [],
    paused_user_ids: [],
    target_deal_field: 'sales_development',
  });

  const handleOpenNew = () => {
    setEditingRule(null);
    setFormData({
      name: '',
      description: '',
      is_active: true,
      priority: 0,
      market: '',
      business_unit: '',
      source: '',
      origem: '',
      target_department_id: '',
      target_role_id: '',
      assignment_strategy: 'round_robin',
      selected_user_ids: [],
      paused_user_ids: [],
      target_deal_field: 'sales_development',
    });
    setShowDialog(true);
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      description: rule.description || '',
      is_active: rule.is_active,
      priority: rule.priority,
      market: rule.market || '',
      business_unit: rule.business_unit || '',
      source: rule.source || '',
      origem: rule.origem || '',
      target_department_id: rule.target_department_id || '',
      target_role_id: rule.target_role_id || '',
      assignment_strategy: rule.assignment_strategy,
      selected_user_ids: rule.metadata?.selected_user_ids || [],
      paused_user_ids: rule.metadata?.paused_user_ids || [],
      target_deal_field: rule.target_deal_field || 'sales_development',
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }

    // Validação: precisa ter usuários selecionados OU department/role
    if (formData.selected_user_ids.length === 0 && !formData.target_department_id && !formData.target_role_id) {
      toast.error('Selecione usuários específicos OU department/role');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        is_active: formData.is_active,
        priority: formData.priority,
        market: formData.market || null,
        business_unit: formData.business_unit || null,
        source: formData.source || null,
        origem: formData.origem || null,
        target_department_id: formData.target_department_id || null,
        target_role_id: formData.target_role_id || null,
        assignment_strategy: formData.assignment_strategy,
        target_deal_field: formData.target_deal_field,
        metadata: {
          ...(editingRule?.metadata || {}),
          selected_user_ids: formData.selected_user_ids,
          paused_user_ids: formData.paused_user_ids,
        },
      };

      if (editingRule) {
        const { error } = await supabase
          .from('assignment_rules')
          .update({ ...payload, updated_at: new Date().toISOString() })
          .eq('id', editingRule.id);

        if (error) throw error;
        toast.success('Regra atualizada com sucesso');
      } else {
        const { error } = await supabase.from('assignment_rules').insert(payload);

        if (error) throw error;
        toast.success('Regra criada com sucesso');
      }

      queryClient.invalidateQueries(['assignment-rules']);
      setShowDialog(false);
    } catch (error) {
      console.error('Error saving rule:', error);
      toast.error(error.message || 'Failed to save rule');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (rule) => {
    try {
      const { error } = await supabase
        .from('assignment_rules')
        .update({ is_active: !rule.is_active, updated_at: new Date().toISOString() })
        .eq('id', rule.id);

      if (error) throw error;
      queryClient.invalidateQueries(['assignment-rules']);
      toast.success(`Regra ${!rule.is_active ? 'ativada' : 'desativada'}`);
    } catch (error) {
      console.error('Error toggling rule:', error);
      toast.error('Falha ao atualizar regra');
    }
  };

  const handleChangePriority = async (rule, direction) => {
    const newPriority = direction === 'up' ? rule.priority + 1 : rule.priority - 1;
    try {
      const { error } = await supabase
        .from('assignment_rules')
        .update({ priority: newPriority, updated_at: new Date().toISOString() })
        .eq('id', rule.id);

      if (error) throw error;
      queryClient.invalidateQueries(['assignment-rules']);
      toast.success('Prioridade atualizada');
    } catch (error) {
      console.error('Error changing priority:', error);
      toast.error('Falha ao atualizar prioridade');
    }
  };

  return (
    <div className="bg-background p-6 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate(createPageUrl('Settings'))}
              className="gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Regras de Atribuição</h1>
              <p className="text-muted-foreground">
                Configure regras para atribuição automática de leads
              </p>
            </div>
          </div>
          <Button onClick={handleOpenNew} className="gap-2">
            <Plus className="w-4 h-4" />
            Nova Regra
          </Button>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <GitBranch className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle>Regras de Atribuição</CardTitle>
                <CardDescription>
                  Regras são aplicadas por prioridade (maior prioridade primeiro)
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">
                Carregando regras...
              </div>
            ) : rules.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhuma regra encontrada. Crie sua primeira regra para começar.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Prioridade</TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>Condições</TableHead>
                    <TableHead>Destino</TableHead>
                    <TableHead>Estratégia</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rules.map((rule) => (
                    <TableRow key={rule.id}>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleChangePriority(rule, 'up')}
                          >
                            <ArrowUp className="w-3 h-3" />
                          </Button>
                          <span className="font-mono">{rule.priority}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleChangePriority(rule, 'down')}
                          >
                            <ArrowDown className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{rule.name}</TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {rule.market && <Badge variant="outline">Market: {rule.market}</Badge>}
                          {rule.business_unit && <Badge variant="outline">BU: {rule.business_unit}</Badge>}
                          {rule.source && <Badge variant="outline">Source: {rule.source}</Badge>}
                          {rule.origem && <Badge variant="outline">Origem: {rule.origem}</Badge>}
                          {!rule.market && !rule.source && !rule.origem && (
                            <Badge variant="secondary">All</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          {rule.target_department && (
                            <Badge>Dept: {rule.target_department.name}</Badge>
                          )}
                          {rule.target_role && <Badge>Role: {rule.target_role.name}</Badge>}
                          <Badge variant="default">
                            {rule.target_deal_field === 'sales_consultant' ? 'Sales Consultant' : 'SDR'}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <Badge variant="outline">
                            {rule.assignment_strategy === 'round_robin'
                              ? 'Round Robin'
                              : 'Aleatório'}
                          </Badge>
                          {rule.metadata?.selected_user_ids && rule.metadata.selected_user_ids.length > 0 && (
                            <div className="text-xs text-muted-foreground">
                              {rule.metadata.selected_user_ids.map(id => getUserName(id)).join(', ')}
                            </div>
                          )}
                          {rule.metadata?.paused_user_ids && rule.metadata.paused_user_ids.length > 0 && (
                            <div className="text-xs text-orange-500">
                              {rule.metadata.paused_user_ids.length} pausado(s)
                            </div>
                          )}
                          {rule.metadata?.last_assigned_index !== undefined && (
                            <div className="text-xs text-muted-foreground">
                              Índice: {rule.metadata.last_assigned_index}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={rule.is_active ? 'default' : 'secondary'}>
                          {rule.is_active ? 'Ativa' : 'Inativa'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(rule)}
                            className="gap-1"
                          >
                            <Edit className="w-3 h-3" />
                            Editar
                          </Button>
                          <Button
                            variant={rule.is_active ? 'secondary' : 'default'}
                            size="sm"
                            onClick={() => handleToggleActive(rule)}
                          >
                            {rule.is_active ? 'Desativar' : 'Ativar'}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingRule ? 'Editar Regra' : 'Nova Regra de Atribuição'}
              </DialogTitle>
              <DialogDescription>
                {editingRule
                  ? 'Atualize a configuração da regra abaixo.'
                  : 'Crie uma nova regra para atribuição automática de leads.'}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">
                    Nome da Regra <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="ex: Leads BR - Vendas"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Descrição</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descrição opcional da regra"
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="priority">Prioridade</Label>
                    <Input
                      id="priority"
                      type="number"
                      value={formData.priority}
                      onChange={(e) =>
                        setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })
                      }
                    />
                    <p className="text-xs text-muted-foreground">Maior valor = maior prioridade</p>
                  </div>

                  <div className="flex items-center justify-between pt-8">
                    <Label htmlFor="is_active">Ativa</Label>
                    <Switch
                      id="is_active"
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    />
                  </div>
                </div>
              </div>

              {/* Conditions */}
              <div className="border-t pt-4 space-y-4">
                <h3 className="font-semibold">Condições (Deixe vazio para aplicar a todos)</h3>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="market">Market</Label>
                    <Select
                      value={formData.market}
                      onValueChange={(value) =>
                        setFormData({ ...formData, market: value === 'none' ? '' : value })
                      }
                    >
                      <SelectTrigger id="market">
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Todos</SelectItem>
                        {MARKETS.map((market) => (
                          <SelectItem key={market} value={market}>
                            {market}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="business_unit">Business Unit</Label>
                    <Select
                      value={formData.business_unit}
                      onValueChange={(value) =>
                        setFormData({ ...formData, business_unit: value === 'none' ? '' : value })
                      }
                    >
                      <SelectTrigger id="business_unit">
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Todos</SelectItem>
                        {BUSINESS_UNITS.map((bu) => (
                          <SelectItem key={bu.value} value={bu.value}>
                            {bu.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">

                  <div className="space-y-2">
                    <Label htmlFor="source">Source</Label>
                    <Select
                      value={formData.source}
                      onValueChange={(value) =>
                        setFormData({ ...formData, source: value === 'none' ? '' : value })
                      }
                    >
                      <SelectTrigger id="source">
                        <SelectValue placeholder="All" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">All</SelectItem>
                        {SOURCES.map((source) => (
                          <SelectItem key={source} value={source}>
                            {source}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="origem">Origem</Label>
                    <Input
                      id="origem"
                      value={formData.origem}
                      onChange={(e) => setFormData({ ...formData, origem: e.target.value })}
                      placeholder="ex: landing_page"
                    />
                  </div>
                </div>
              </div>

              {/* Assignment Target */}
              <div className="border-t pt-4 space-y-4">
                <h3 className="font-semibold">
                  Atribuir para <span className="text-destructive">*</span>
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="department">Department</Label>
                    <Select
                      value={formData.target_department_id}
                      onValueChange={(value) =>
                        setFormData({
                          ...formData,
                          target_department_id: value === 'none' ? '' : value,
                        })
                      }
                    >
                      <SelectTrigger id="department">
                        <SelectValue placeholder="Selecione department" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nenhum</SelectItem>
                        {departments.map((dept) => (
                          <SelectItem key={dept.id} value={dept.id}>
                            {dept.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="role">Role</Label>
                    <Select
                      value={formData.target_role_id}
                      onValueChange={(value) =>
                        setFormData({
                          ...formData,
                          target_role_id: value === 'none' ? '' : value,
                        })
                      }
                    >
                      <SelectTrigger id="role">
                        <SelectValue placeholder="Selecione role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nenhum</SelectItem>
                        {roles.map((role) => (
                          <SelectItem key={role.id} value={role.id}>
                            {role.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <p className="text-xs text-muted-foreground">
                  Selecione usuários específicos OU department/role. Se usuários forem selecionados, apenas eles receberão leads.
                </p>

                <div className="space-y-2">
                  <Label htmlFor="strategy">Estratégia de Atribuição</Label>
                  <Select
                    value={formData.assignment_strategy}
                    onValueChange={(value) =>
                      setFormData({ ...formData, assignment_strategy: value })
                    }
                  >
                    <SelectTrigger id="strategy">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STRATEGIES.map((strategy) => (
                        <SelectItem key={strategy.value} value={strategy.value}>
                          {strategy.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Round Robin distribui leads igualmente. Aleatório atribui randomicamente. Aplica-se aos usuários selecionados ou aos do department/role.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="target_deal_field">Atribuir como</Label>
                  <Select
                    value={formData.target_deal_field}
                    onValueChange={(value) =>
                      setFormData({ ...formData, target_deal_field: value })
                    }
                  >
                    <SelectTrigger id="target_deal_field">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sales_development">Sales Development (SDR)</SelectItem>
                      <SelectItem value="sales_consultant">Sales Consultant</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Define se o usuário será atribuído como SDR ou como Sales Consultant no Deal.
                  </p>
                </div>

                {/* Seletor de usuários */}
                <div className="space-y-2">
                  <Label>
                    Selecionar Usuários (Opcional)
                  </Label>
                  <div>
                    <MultiSelect
                      options={users.map((user) => ({
                        value: user.id,
                        label:
                          user.display_name ||
                          `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
                          user.email ||
                          'Usuário sem nome',
                      }))}
                      value={formData.selected_user_ids}
                      onChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          selected_user_ids: value,
                        }))
                      }
                      placeholder="Selecione os usuários..."
                      emptyText="Nenhum usuário encontrado."
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Se usuários forem selecionados, apenas eles receberão leads (ignorando department/role). A estratégia escolhida será aplicada entre eles.
                  </p>
                </div>

                {/* Pausar usuários */}
                {formData.selected_user_ids.length > 0 && (
                  <div className="space-y-2">
                    <Label>
                      Usuários Pausados (não recebem leads, mas mantêm posição no round-robin)
                    </Label>
                    <div>
                      <MultiSelect
                        options={formData.selected_user_ids.map((userId) => ({
                          value: userId,
                          label: getUserName(userId),
                        }))}
                        value={formData.paused_user_ids}
                        onChange={(value) =>
                          setFormData((prev) => ({
                            ...prev,
                            paused_user_ids: value,
                          }))
                        }
                        placeholder="Selecione usuários para pausar..."
                        emptyText="Nenhum usuário selecionado."
                      />
                    </div>
                    <p className="text-xs text-orange-500">
                      Usuários pausados são ignorados na distribuição mas não perdem sua posição no round-robin.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? 'Saving...' : editingRule ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
